import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { authenticatedFetch } from '../utils/auth'; 
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle, Loader2, ArrowRight, LogIn, AlertOctagon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VerifyInvitePage = () => {
    const { keycloak, initialized } = useKeycloak();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation(); // Get current location for redirect
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [needsLogin, setNeedsLogin] = useState(false); // State to track if login is needed
    const [isUnauthorized, setIsUnauthorized] = useState(false); // State to track if user is unauthorized

    useEffect(() => {
        // Wait for keycloak to be initialized before checking auth status
        if (!initialized) {
            return; 
        }

        const token = searchParams.get('token');

        if (!token) {
            setError(t('verifyInvite.errors.noToken'));
            setIsLoading(false);
            setNeedsLogin(false);
            return;
        }

        const verifyToken = async () => {
            setIsLoading(true);
            setError(null);
            setSuccessMessage(null);
            setNeedsLogin(false);
            setIsUnauthorized(false);

            // Check if user is authenticated *before* calling the protected API
            if (!keycloak.authenticated) {
                setNeedsLogin(true);
                setIsLoading(false);
                return; // Stop here, wait for user to log in
            }

            // First check if the user is authorized to use the application
            try {
                console.log('Checking user authorization before verifying invite token');
                const authCheckResponse = await authenticatedFetch(
                    '/api/users/check-authorization',
                    { method: 'GET' },
                    keycloak.token
                );
                
                // If the user is not authorized, stop here
                if (!authCheckResponse.ok) {
                    console.error('User is not authorized to verify invites');
                    setIsUnauthorized(true);
                    setIsLoading(false);
                    return;
                }
                
                const authData = await authCheckResponse.json();
                if (!authData.is_authorized) {
                    console.error('User is not in the whitelist');
                    setIsUnauthorized(true);
                    toast.error(t('verifyInvite.errors.unauthorized'));
                    setIsLoading(false);
                    return;
                }

                console.log('User is authorized, proceeding with invite verification');
            } catch (error) {
                console.error('Authorization check failed:', error);
                setIsUnauthorized(true);
                setError(t('verifyInvite.errors.unauthorized'));
                setIsLoading(false);
                return;
            }

            // --- User is authenticated and authorized, proceed with verification API call --- 
            try {
                const response = await authenticatedFetch(
                    `api/teams/verify-invite/${token}`, // Use GET with token in path
                    { method: 'GET' }, // Specify GET method
                    keycloak.token
                );
                
                if (!response.ok) {
                    let errorDetail = t('verifyInvite.errors.generic');
                    try {
                         const errorData = await response.json();
                         errorDetail = errorData.detail || errorDetail;
                    } catch (e) { /* Ignore JSON parsing error if body is empty */ }
                    
                    // Handle specific status codes
                    if (response.status === 404) {
                        errorDetail = t('verifyInvite.errors.invalidOrExpired');
                    } else if (response.status === 400) {
                         errorDetail = t('verifyInvite.errors.expired');
                    } else if (response.status === 403) {
                         errorDetail = t('verifyInvite.errors.wrongEmail');
                    } else if (response.status === 503 || response.status === 401) {
                        // Handle unauthorized user scenario
                        setIsUnauthorized(true);
                        errorDetail = t('verifyInvite.errors.unauthorized');
                        toast.error(t('verifyInvite.errors.unauthorized'));
                    }
                    throw new Error(errorDetail);
                }

                // const result = await response.json(); // Get message from backend
                // setSuccessMessage(result.message || t('verifyInvite.successMessage'));
                setSuccessMessage(t('verifyInvite.successMessage')); // Use generic success message for now

                toast.success(t('verifyInvite.addedToTeam'));
                // Redirect to dashboard after a short delay
                setTimeout(() => navigate('/dashboards', { replace: true }), 2000);

            } catch (err) {
                setError(err.message || t('verifyInvite.errors.generic'));
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();

    // Rerun effect if keycloak initialization or authentication state changes
    }, [initialized, keycloak.authenticated, searchParams, navigate, t]); 

    // Function to handle login -> redirect back here
    const handleLogin = () => {
        if (keycloak && !keycloak.authenticated) {
            // Get the current path including query string
            const currentPath = location.pathname + location.search;
            keycloak.login({ redirectUri: window.location.origin + currentPath });
        }
    };

    // Function to handle request for access
    const handleRequestAccess = () => {
        // Here you would typically send an API request to request access
        // For now, just show a toast message
        toast.info(t('verifyInvite.accessRequested'));
        // Redirect to dashboard after a short delay
        setTimeout(() => navigate('/dashboards', { replace: true }), 2000);
    };

    // --- Render Logic --- //
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                <h1 className="text-2xl font-semibold mb-6 text-gray-800">{t('verifyInvite.title')}</h1>

                {/* Loading State (includes Keycloak initializing) */}
                {isLoading && (
                    <div className="flex flex-col items-center">
                        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                        <p className="text-gray-600">{initialized ? t('verifyInvite.verifying') : t('common.loading')}...</p>
                    </div>
                )}

                {/* Needs Login State */}
                {!isLoading && needsLogin && (
                     <div className="flex flex-col items-center">
                        <LogIn className="w-12 h-12 text-blue-500 mb-4" />
                        <p className="text-gray-700 font-medium mb-2">{t('verifyInvite.loginRequiredTitle')}</p>
                        <p className="text-gray-600 mb-6">{t('verifyInvite.loginRequiredMessage')}</p>
                        <button 
                            onClick={handleLogin}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            {t('verifyInvite.loginButton')}
                            <ArrowRight size={16} className="ml-2" />
                        </button>
                    </div>
                )}

                {/* Unauthorized State */}
                {!isLoading && !needsLogin && isUnauthorized && (
                    <div className="flex flex-col items-center">
                        <AlertOctagon className="w-12 h-12 text-orange-500 mb-4" />
                        <p className="text-orange-600 font-medium mb-2">{t('verifyInvite.unauthorizedTitle')}</p>
                        <p className="text-gray-600 mb-6">{t('verifyInvite.unauthorizedMessage')}</p>
                    </div>
                )}

                {/* Error State (not unauthorized) */}
                {!isLoading && !needsLogin && error && !isUnauthorized && (
                    <div className="flex flex-col items-center">
                        <XCircle className="w-12 h-12 text-red-500 mb-4" />
                        <p className="text-red-600 font-medium mb-2">{t('verifyInvite.errorTitle')}</p>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <Link 
                            to="/dashboards"
                            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            {t('verifyInvite.backToDashboards')}
                        </Link>
                    </div>
                )}

                {/* Success State */}
                {!isLoading && !needsLogin && successMessage && !error && (
                    <div className="flex flex-col items-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
                        <p className="text-green-600 font-medium mb-2">{t('verifyInvite.successTitle')}</p>
                        <p className="text-gray-600 mb-6">{successMessage}</p>
                        {/* Button might not be needed here as it auto-redirects */}
                         <p className="text-sm text-gray-500">{t('verifyInvite.redirecting')}...</p> 
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyInvitePage; 